import { useState, useEffect, useMemo } from "react";
import {
  Tabs,
  TabList,
  Tab,
  ActionButton,
  Text,
  createIcon,
} from "@react-spectrum/s2";
import { useMatch, useNavigate } from "react-router";
import PropTypes from "prop-types";
import { useStore } from "zustand";

import { store } from "../store";
import { useIsAdmin } from "@app/hooks/useRoles";

import HomeIcon from "@react-spectrum/s2/icons/Home";
import FolderIcon from "@react-spectrum/s2/icons/Folder";
import SlideshowIcon from "@react-spectrum/s2/icons/Slideshow";
import BetaAppIcon from "@react-spectrum/s2/icons/BetaApp";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const PanelIcon = createIcon((props) => {
  const { state, isHovered, ...otherProps } = props;

  return (
    <svg viewBox="0 0 20 20" fill="var(--iconPrimary)" {...otherProps}>
      <path
        d="M15.75 18H4.25C3.00977 18 2 16.9907 2 15.75V4.25C2 3.00928 3.00977 2 4.25 2H15.75C16.9902 2 18 3.00928 18 4.25V15.75C18 16.9907 16.9902 18 15.75 18ZM4.25 3.5C3.83691 3.5 3.5 3.83643 3.5 4.25V15.75C3.5 16.1636 3.83691 16.5 4.25 16.5H15.75C16.1631 16.5 16.5 16.1636 16.5 15.75V4.25C16.5 3.83643 16.1631 3.5 15.75 3.5H4.25Z"
        fill="var(--iconPrimary)"
      />
      <rect
        x={5}
        y={5}
        rx={0.5}
        height={10}
        className={style({
          transition: "[width]",
          transitionDuration: 300,
          width: {
            default: "[1.5px]",
            lg: "[5px]",
            state: {
              expanded: "[5px]",
              collapsed: "[1.5px]",
            },
            isHovered: {
              default: "[5px]",
              lg: "[1.5px]",
              state: {
                expanded: "[1.5px]",
                collapsed: "[5px]",
              },
            },
          },
        })({ state, isHovered })}
      />
    </svg>
  );
});

PanelIcon.propTypes = {
  state: PropTypes.oneOf(["expanded", "collapsed"]).isRequired,
  isHovered: PropTypes.bool.isRequired,
};

const PanelToggleButton = ({ isCollapsed, onPress }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <ActionButton
      isQuiet
      // size="S"
      onPress={onPress}
      onHoverChange={setIsHovered}
      aria-label={
        isCollapsed ? "Expand side navigation" : "Collapse side navigation"
      }
      styles={style({
        marginTop: "auto",
        marginStart: "auto",
        marginEnd: {
          default: 0,
          isCollapsed: "auto",
        },
      })({ isCollapsed })}
    >
      <PanelIcon
        isHovered={isHovered}
        state={isCollapsed ? "collapsed" : "expanded"}
      />
    </ActionButton>
  );
};

PanelToggleButton.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onPress: PropTypes.func.isRequired,
};

export const SideNav = ({ styles }) => {
  const isHome = useMatch("/");
  const isStories = useMatch("/stories/*");
  const isProjects = useMatch("/projects/*");
  const isTemplates = useMatch("/templates/*");
  const isAdminRoute = useMatch("/admin/*");
  const isCopilot = useMatch("/_copilot/*");

  const isAdmin = useIsAdmin();

  const isSideNavCollapsed = useStore(
    store,
    (state) => state.isSideNavCollapsed,
  );
  const setSideNavCollapsed = useStore(
    store,
    (state) => state.setSideNavCollapsed,
  );

  const navigate = useNavigate();

  const [selectedKey, setSelectedKey] = useState("home");

  useEffect(() => {
    if (isHome) {
      setSelectedKey("home");
    } else if (isStories) {
      setSelectedKey("stories");
    } else if (isProjects) {
      setSelectedKey("projects");
    } else if (isTemplates) {
      setSelectedKey("templates");
    } else if (isAdminRoute) {
      setSelectedKey("admin");
    } else if (isCopilot) {
      setSelectedKey("copilot");
    }
  }, [isHome, isStories, isProjects, isTemplates, isAdminRoute, isCopilot]);

  const paths = useMemo(
    () => [
      { key: "home", path: "/", match: "/", name: "Home", icon: <HomeIcon /> },
      {
        key: "projects",
        path: "/projects",
        match: "/projects/*",
        name: "Projects",
        icon: <FolderIcon />,
      },
      // {
      //   key: "stories",
      //   path: "/stories",
      //   match: "/stories/*",
      //   name: "Stories",
      //   icon: <SlideshowIcon />,
      // },
      // {
      //   key: "stories",
      //   path: "/stories",
      //   match: "/stories/*",
      //   name: "Stories",
      //   icon: <ProjectIcon />,
      // },
      // {
      //   key: "templates",
      //   path: "/templates",
      //   match: "/templates/*",
      //   name: "Templates",
      //   icon: <TemplateIcon />,
      // },
      ...(isAdmin
        ? [
            {
              key: "admin",
              path: "/admin",
              match: "/admin/*",
              name: "Admin",
              icon: <BetaAppIcon />,
            },
          ]
        : []),
    ],
    [isAdmin],
  );

  return (
    <div
      className={
        style({
          // display: "flex",
          flexDirection: "column",
          height: "full",
          width: {
            default: 32,
            lg: 160,
            state: {
              expanded: 160,
              collapsed: 32,
            },
          },
          overflow: "clip",
          transition: "[width]",
          transitionDuration: 300,
        })({ state: isSideNavCollapsed ? "collapsed" : "expanded" }) + styles
      }
    >
      <Tabs
        aria-label="Side Navigation"
        selectedKey={selectedKey}
        orientation="vertical"
        density="compact"
      >
        <TabList UNSAFE_className={style({ marginStart: -4 }) + " __SideNav"}>
          {paths.map(({ key, path, match, name, icon }) => (
            <Tab key={key} id={key} onPress={() => navigate(path)}>
              {icon}
              {
                <Text
                  styles={style({
                    opacity: {
                      default: 0,
                      lg: 1,
                      state: {
                        expanded: 1,
                        collapsed: 0,
                      },
                    },
                    width: {
                      default: 0,
                      lg: "fit",
                      state: {
                        expanded: "fit",
                        collapsed: 0,
                      },
                    },
                    transition: "[opacity, width]",
                    transitionDuration: 300,
                  })({
                    state: isSideNavCollapsed ? "collapsed" : "expanded",
                  })}
                >
                  {name}
                </Text>
              }
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <PanelToggleButton
        isCollapsed={isSideNavCollapsed}
        onPress={() => setSideNavCollapsed(!isSideNavCollapsed)}
      />
    </div>
  );
};

SideNav.propTypes = {
  styles: PropTypes.string,
};
